<script lang="ts">
  import type { Faq } from '../lib/types';
  import { normaliseForSearch } from '../lib/text';

  interface Props {
    faqs: Faq[];
    searchPlaceholder: string;
    emptyLabel: string;
    startChatLabel: string;
    welcomeTitle?: string;
    welcomePrompt?: string;
    assistantName?: string;
    assistantAvatarUrl?: string;
    assistantTagline?: string;
    onstartchat: () => void;
  }

  let {
    faqs,
    searchPlaceholder,
    emptyLabel,
    startChatLabel,
    welcomeTitle,
    welcomePrompt,
    assistantName,
    assistantAvatarUrl,
    assistantTagline,
    onstartchat,
  }: Props = $props();

  // Assistant-centered welcome block overrides the generic title/prompt when
  // a tenant sets up a persona. Keeps the component backwards compatible.
  const hasAssistantWelcome = $derived(
    Boolean(
      (assistantName && assistantName.trim()) ||
        assistantAvatarUrl ||
        assistantTagline,
    ),
  );
  const assistantDisplayName = $derived(
    assistantName && assistantName.trim().length > 0
      ? assistantName
      : 'tu asistente',
  );
  const assistantInitial = $derived(
    assistantDisplayName.charAt(0).toUpperCase() || '?',
  );
  const assistantHeadline = $derived(`Hola, soy ${assistantDisplayName}`);
  const assistantSubline = $derived(
    assistantTagline ??
      'Estoy aquí para ayudarte. Elige una pregunta o escríbeme.',
  );

  let query = $state('');
  let expandedId = $state<string | null>(null);

  const filtered = $derived.by(() => {
    const q = normaliseForSearch(query);
    if (!q) return faqs;
    return faqs.filter((f) => {
      const haystack = normaliseForSearch(f.question + ' ' + f.answer + ' ' + f.tags.join(' '));
      return haystack.includes(q);
    });
  });

  function toggle(id: string): void {
    expandedId = expandedId === id ? null : id;
  }

  function teaser(answer: string): string {
    const trimmed = answer.trim().replace(/\s+/g, ' ');
    if (trimmed.length <= 120) return trimmed;
    return trimmed.slice(0, 117).trimEnd() + '…';
  }
</script>

<div class="hd-faq">
  <div class="hd-faq__scroll">
    {#if hasAssistantWelcome}
      <div class="hd-faq__welcome hd-faq__welcome--assistant">
        <div class="hd-faq__avatar">
          {#if assistantAvatarUrl}
            <img src={assistantAvatarUrl} alt="" />
          {:else}
            <span class="hd-faq__avatar-initial">{assistantInitial}</span>
          {/if}
          <span class="hd-faq__presence" aria-hidden="true"></span>
        </div>
        <h2 class="hd-faq__welcome-title">{assistantHeadline}</h2>
        <p class="hd-faq__welcome-prompt">{assistantSubline}</p>
      </div>
    {:else if welcomeTitle || welcomePrompt}
      <div class="hd-faq__welcome">
        {#if welcomeTitle}
          <h2 class="hd-faq__welcome-title">{welcomeTitle}</h2>
        {/if}
        {#if welcomePrompt}
          <p class="hd-faq__welcome-prompt">{welcomePrompt}</p>
        {/if}
      </div>
    {/if}

    <div class="hd-faq__search">
      <svg class="hd-faq__search-icon" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" stroke-width="2" />
        <path d="m20 20-3.5-3.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
      </svg>
      <input
        type="search"
        placeholder={searchPlaceholder}
        bind:value={query}
        aria-label={searchPlaceholder}
      />
    </div>

    <ul class="hd-faq__list" role="list">
      {#each filtered as faq (faq.id)}
        {@const isOpen = expandedId === faq.id}
        <li class="hd-faq__item" class:hd-faq__item--open={isOpen}>
          <button
            type="button"
            class="hd-faq__q"
            aria-expanded={isOpen}
            onclick={() => toggle(faq.id)}
          >
            <span class="hd-faq__chip" aria-hidden="true">?</span>
            <span class="hd-faq__text">
              <span class="hd-faq__question">{faq.question}</span>
              {#if !isOpen}
                <span class="hd-faq__teaser">{teaser(faq.answer)}</span>
              {/if}
            </span>
            <svg class="hd-faq__chev" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
          <div class="hd-faq__a-wrap" aria-hidden={!isOpen}>
            <div class="hd-faq__a">{faq.answer}</div>
          </div>
        </li>
      {:else}
        <li class="hd-faq__empty">{emptyLabel}</li>
      {/each}
    </ul>
  </div>

  <div class="hd-faq__cta">
    <button type="button" class="hd-faq__cta-btn" onclick={onstartchat}>
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M20 2H4a2 2 0 0 0-2 2v14l4-4h14a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2Z"
          fill="currentColor"
        />
      </svg>
      <span>{startChatLabel}</span>
    </button>
  </div>
</div>

<style>
  .hd-faq {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    background: var(--hd-surface, #ffffff);
  }
  .hd-faq__scroll {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  .hd-faq__welcome {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .hd-faq__welcome--assistant {
    align-items: center;
    text-align: center;
    gap: 10px;
    padding: 4px 0 8px;
  }
  .hd-faq__avatar {
    position: relative;
    width: 56px;
    height: 56px;
    border-radius: 9999px;
    background: rgb(var(--hd-primary-rgb, 37 99 235) / 0.1);
    color: var(--hd-primary, #2563eb);
    display: grid;
    place-items: center;
    overflow: visible;
    flex: 0 0 auto;
    box-shadow: inset 0 0 0 1px rgb(var(--hd-primary-rgb, 37 99 235) / 0.15);
  }
  .hd-faq__avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 9999px;
  }
  .hd-faq__avatar-initial {
    font-family: ui-monospace, 'SF Mono', Menlo, Consolas, 'Liberation Mono',
      monospace;
    font-size: 22px;
    font-weight: 600;
    line-height: 1;
  }
  .hd-faq__presence {
    position: absolute;
    bottom: 2px;
    right: 2px;
    width: 12px;
    height: 12px;
    border-radius: 9999px;
    background: #22c55e;
    box-shadow: 0 0 0 2px #ffffff;
  }
  .hd-faq__welcome--assistant .hd-faq__welcome-title {
    font-size: 18px;
    font-weight: 600;
  }
  .hd-faq__welcome--assistant .hd-faq__welcome-prompt {
    font-size: 13px;
    font-weight: 400;
    max-width: 280px;
  }
  .hd-faq__welcome-title {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: var(--hd-fg, #0f172a);
    line-height: 1.2;
  }
  .hd-faq__welcome-prompt {
    margin: 0;
    font-size: 14px;
    font-weight: 400;
    color: var(--hd-fg-muted, #64748b);
    line-height: 1.5;
  }
  .hd-faq__search {
    position: relative;
    display: flex;
    align-items: center;
  }
  .hd-faq__search-icon {
    position: absolute;
    left: 12px;
    width: 16px;
    height: 16px;
    color: var(--hd-fg-subtle, #94a3b8);
    pointer-events: none;
  }
  .hd-faq__search input {
    width: 100%;
    padding: 10px 12px 10px 36px;
    border: 1px solid var(--hd-border, #e2e8f0);
    border-radius: 10px;
    font: inherit;
    font-size: 14px;
    color: var(--hd-fg, #0f172a);
    background: var(--hd-surface, #ffffff);
    box-sizing: border-box;
    transition:
      border-color 180ms cubic-bezier(0.2, 0.8, 0.2, 1),
      box-shadow 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }
  .hd-faq__search input::placeholder {
    color: var(--hd-fg-subtle, #94a3b8);
  }
  .hd-faq__search input:focus {
    outline: none;
    border-color: var(--hd-primary, #2563eb);
    box-shadow: 0 0 0 3px rgb(var(--hd-primary-rgb, 37 99 235) / 0.15);
  }
  .hd-faq__list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .hd-faq__item {
    border: 1px solid var(--hd-border, #e2e8f0);
    border-radius: 10px;
    background: var(--hd-surface, #ffffff);
    overflow: hidden;
    transition:
      border-color 180ms cubic-bezier(0.2, 0.8, 0.2, 1),
      background-color 180ms cubic-bezier(0.2, 0.8, 0.2, 1),
      transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }
  .hd-faq__item:hover {
    border-color: var(--hd-primary, #2563eb);
    background: var(--hd-surface-subtle, #f1f5f9);
  }
  .hd-faq__item:active {
    transform: scale(0.99);
  }
  .hd-faq__item--open {
    border-color: var(--hd-primary, #2563eb);
    background: var(--hd-surface, #ffffff);
  }
  .hd-faq__q {
    display: flex;
    width: 100%;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 14px;
    background: transparent;
    border: none;
    text-align: left;
    font: inherit;
    color: var(--hd-fg, #0f172a);
    cursor: pointer;
  }
  .hd-faq__chip {
    flex: 0 0 auto;
    width: 24px;
    height: 24px;
    border-radius: 9999px;
    background: rgb(var(--hd-primary-rgb, 37 99 235) / 0.1);
    color: var(--hd-primary, #2563eb);
    display: grid;
    place-items: center;
    font-size: 12px;
    font-weight: 600;
    line-height: 1;
    margin-top: 1px;
  }
  .hd-faq__text {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .hd-faq__question {
    font-size: 14px;
    font-weight: 500;
    color: var(--hd-fg, #0f172a);
    line-height: 1.4;
  }
  .hd-faq__teaser {
    font-size: 12px;
    font-weight: 400;
    color: var(--hd-fg-muted, #64748b);
    line-height: 1.45;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
  }
  .hd-faq__chev {
    flex: 0 0 auto;
    width: 16px;
    height: 16px;
    color: var(--hd-fg-subtle, #94a3b8);
    margin-top: 4px;
    transition: transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }
  .hd-faq__item--open .hd-faq__chev {
    transform: rotate(90deg);
  }
  .hd-faq__a-wrap {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }
  .hd-faq__item--open .hd-faq__a-wrap {
    grid-template-rows: 1fr;
  }
  .hd-faq__a {
    min-height: 0;
    overflow: hidden;
    padding: 0 14px 14px 50px;
    font-size: 13px;
    line-height: 1.55;
    color: var(--hd-fg, #0f172a);
    white-space: pre-wrap;
  }
  .hd-faq__empty {
    padding: 24px 12px;
    color: var(--hd-fg-muted, #64748b);
    font-size: 13px;
    text-align: center;
  }
  .hd-faq__cta {
    padding: 12px 16px 16px;
    border-top: 1px solid var(--hd-border, #e2e8f0);
    background: var(--hd-surface, #ffffff);
    flex: 0 0 auto;
  }
  .hd-faq__cta-btn {
    width: 100%;
    padding: 11px 14px;
    border-radius: 10px;
    border: none;
    background: var(--hd-primary, #2563eb);
    color: var(--hd-primary-fg, #ffffff);
    font: inherit;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition:
      filter 180ms cubic-bezier(0.2, 0.8, 0.2, 1),
      transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1),
      box-shadow 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
    box-shadow: 0 1px 2px rgb(15 23 42 / 0.06);
  }
  .hd-faq__cta-btn:hover {
    filter: brightness(0.95);
    box-shadow: 0 4px 12px -4px rgb(var(--hd-primary-rgb, 37 99 235) / 0.35);
  }
  .hd-faq__cta-btn:active {
    transform: scale(0.99);
  }
  .hd-faq__cta-btn:focus-visible {
    outline: 2px solid var(--hd-primary, #2563eb);
    outline-offset: 2px;
  }
  .hd-faq__cta-btn svg {
    width: 16px;
    height: 16px;
  }
  @media (prefers-reduced-motion: reduce) {
    .hd-faq__item,
    .hd-faq__chev,
    .hd-faq__a-wrap,
    .hd-faq__cta-btn,
    .hd-faq__search input {
      transition: none;
    }
  }
</style>
